<template>
  <div
    id="articles-section"
    class="bg-white border-right p-0 m-0 h-100 d-flex flex-column"
    :style="{ 'width': `${sectionWidth}px` }"
    ref="articles-section"
    data-test="articles-section"
  >
    <header
      id="articles-section-header"
      class="px-5 py-4 m-0 bg-secondary d-flex align-items-center justify-content-between"
    >
      <h5
        id="articles-section-header-text"
        class="m-0"
      >
        <strong>
          Landing Pages
        </strong>
      </h5>
      <div>
        <b-button
          v-if="areFiltersApplied"
          variant="tertiary"
          size="sm"
          id="articles-section-filters-reset-button"
          class="mr-1"
          @click="resetFilters()"
          data-test="articles-section-filters-reset-button"
        >
          Reset
        </b-button>
        <b-button
          id="articles-section-filters-button"
          variant="secondary"
          size="sm"
          class="m-0"
          :class="{ 'p-1': sectionWidth < 240 }"
          :style="[ sectionWidth < 240 ? 'min-width: unset' : '' ]"
          @click="toggleArticlesFiltersDisplay()"
          data-test="articles-section-filters-button"
        >
          <span
            v-show="sectionWidth > 240"
          >
            Filters
          </span>
          <i class="fal fa-filter-list" />
        </b-button>
      </div>
    </header>
    <div class="border-bottom">
      <articles-filters
        v-show="isArticlesFiltersDisplayed"
        id="articles-filters"
        ref="articles-filters"
        :class="{ 'share-view': !GET_HAS_CAMPAIGN_ACCESS }"
        :share-mode="!GET_HAS_CAMPAIGN_ACCESS"
        data-test="articles-section-filters"
      />
    </div>
    <section
      id="articles-list-container"
      ref="articles-list-container"
      class="flex-grow-1"
      :class="{'overflow-auto' : hasArticles, 'd-grid': !hasArticles || isLoadingArticles}"
    >
      <div
        v-if="isLoadingArticles"
        class="d-inline align-self-center justify-self-center loader"
      />
      <p
        v-else-if="GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length === 0"
        class="mb-0 d-inline align-self-center justify-self-center"
      >
        No Articles Found
      </p>
      <div
        v-else
        v-for="(article, index) in GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS"
        :key="article.id"
        :data-test="article.id"
      >
        <article-card
          :article="article"
          :index="index"
          :readonly-mode="GET_IS_CAMPAIGN_ENDED"
          :share-mode="!GET_HAS_CAMPAIGN_ACCESS"
          :ref="`article-card-${index}`"
        />
      </div>
    </section>
    <section
      v-if="hasArticles"
      id="articles-pagination-section"
      class="border-top"
    >
      <base-pagination
        :items-count="pagingTotalItemsCount"
        :current-page="paging.currentPage"
        :per-page-selected="paging.perPageSelected"
        :hide-page-select="true"
        items-name="content items"
        @pagingUpdate="updatePaging(...arguments)"
        :disabled="paging.isDisabled"
        data-test="creatives-page-articles-pagination"
      />
    </section>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import helper from '@/core/helper';

import ArticlesFilters from '@/modules/content/components/CreativesPage/ArticlesSection/ArticlesFilters';
import ArticleCard from '@/modules/content/components/CreativesPage/ArticlesSection/ArticleCard';
import BasePagination from '@/components/BasePagination.vue';

export default {
  name: 'ArticlesSection',
  components: {
    BasePagination,
    ArticlesFilters,
    ArticleCard,
  },
  data() {
    return {
      isArticlesFiltersDisplayed: false,
      paging: {
        isDisabled: false,
        currentPage: 1,
        perPageSelected: 20,
      },
      sectionWidth: 0,
      resizeObserver: null,
      scrolledAmount: 0,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'isLoadingArticles',
      'isSidebarInputFocused',
      'selectedCreativeId',
      'contentItemsQueryParams',
      'totalItems',
      'selectedArticleUuid',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
      campaignTypes.GET_IS_VIEW_TYPE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
    ]),
    hasArticles() {
      return this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length > 0;
    },
    areFiltersApplied() {
      return this.contentItemsQueryParams.hasOwnProperty('search')
        || this.contentItemsQueryParams.hasOwnProperty('states')
        || this.contentItemsQueryParams.hasOwnProperty('sort');
    },
    pagingTotalItemsCount() {
      return this.totalItems;
    },
    isKeyboardNavigationBlocked() {
      return this.isSidebarInputFocused;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_SHOWCASE_ARTICLES,
      contentTypes.SET_CONTENT_ITEM_QUERY_PARAM,
      contentTypes.RESET_CONTENT_ITEMS_QUERY_PARAMS,
      contentTypes.FETCH_CONTENT_COMMENTS_COUNT,
      contentTypes.FETCH_SHARED_CONTENT_ITEMS,
      contentTypes.SET_SELECTED_ARTICLE_UUID,
    ]),
    async resetFilters() {
      this.RESET_CONTENT_ITEMS_QUERY_PARAMS({});
      await this.SET_CONTENT_ITEM_QUERY_PARAM({
        page: 1,
        pageSize: this.paging.perPageSelected,
      });
      this.fetchArticles();
    },
    toggleArticlesFiltersDisplay() {
      const element = this.$refs['articles-filters']?.$el;
      if (this.isArticlesFiltersDisplayed) {
        element.classList.add('close-filters');
        element?.addEventListener('animationend', () => {
          element?.classList.remove('open-filters');
          element?.classList.remove('close-filters');
          this.isArticlesFiltersDisplayed = false;
        }, { once: true });
      } else {
        this.isArticlesFiltersDisplayed = true;
        element.classList.add('open-filters');
      }
    },
    fetchArticles(resetPaging = true) {
      this.setPaging({ isDisabled: true });
      return this.FETCH_SHOWCASE_ARTICLES()
        .then(async () => {
          if (this.GET_HAS_CAMPAIGN_ACCESS) {
            this.FETCH_SHARED_CONTENT_ITEMS();
          }
          const comments = await this.FETCH_CONTENT_COMMENTS_COUNT({ campaignId: this.campaign.id, creativeId: this.selectedCreativeId });
          if (this.GET_IS_VIEW_TYPE('Showcase')) {
            if (comments[this.selectedArticleUuid] && comments[this.selectedArticleUuid].count > 0) {
              this.$emit('openCommentsTab');
            }
          }
          if (resetPaging) this.setPaging({ currentPage: 1 });
          this.setPaging({ isDisabled: false });
        });
    },
    async updatePaging(payload) {
      const isSameCurrentPage = payload.page === this.paging.currentPage;
      if (isSameCurrentPage) return;
      this.setPaging({
        currentPage: payload.page,
      });
      await this.SET_CONTENT_ITEM_QUERY_PARAM({ page: payload.page });
      this.fetchArticles(false);
    },
    setPaging(payload) {
      if (helper.objLength(payload).length === 0) return;
      for (const property in payload) {
        if (!payload.hasOwnProperty(property)) continue;
        if (this.paging.hasOwnProperty(property)) this.paging[property] = payload[property];
      }
    },
    setSectionWidth() {
      this.sectionWidth = window.innerWidth / 6;
    },
    async handleKeydown(event) {
      if (!this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length > 0 || this.isKeyboardNavigationBlocked) return;
      const index = this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.findIndex((article) => article.encodedExternalId === this.selectedArticleUuid);
      const articleCardComponent = this.$refs[`article-card-${index}`][0];
      const articleCardElement = articleCardComponent ? articleCardComponent.$el : undefined;
      if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      let direction = '';
      if (event.key === 'ArrowUp') {
        if (!index > 0) return;
        event.preventDefault();
        direction = 'up';
        this.scrolledAmount -= articleCardElement.offsetHeight;
        this.SET_SELECTED_ARTICLE_UUID(this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS[index - 1].encodedExternalId);
      }
      if (event.key === 'ArrowDown') {
        if (index >= this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length - 1) return;
        event.preventDefault();
        direction = 'down';
        this.scrolledAmount = articleCardElement.offsetHeight * (index + 2);
        this.SET_SELECTED_ARTICLE_UUID(this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS[index + 1].encodedExternalId);
      }
      this.handleScroll(direction);
    },
    handleScroll(direction) {
      const listContainer = this.$refs['articles-list-container'];
      const containerHeight = listContainer.offsetHeight;
      const view = containerHeight + listContainer.scrollTop;
      const index = this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.findIndex((article) => article.encodedExternalId === this.selectedArticleUuid);
      const articleCardComponent = this.$refs[`article-card-${index}`][0];
      const articleCardElement = articleCardComponent ? articleCardComponent.$el : undefined;
      if (direction === 'up' && this.scrolledAmount > view - containerHeight) return;
      if (direction === 'down' && this.scrolledAmount < view) return;
      let toScroll = 0;
      if (direction === 'down') {
        if (this.scrolledAmount > containerHeight) toScroll = this.scrolledAmount - containerHeight;
        else toScroll = 0;
      } else {
        toScroll = this.scrolledAmount - articleCardElement.offsetHeight;
      }
      listContainer.scroll({
        top: toScroll,
        left: 0,
        behavior: 'auto',
      });
    },
  },
  async mounted() {
    const { contentItemId } = this.$route.query;
    if (contentItemId) this.SET_SELECTED_ARTICLE_UUID(contentItemId);
    this.setSectionWidth();
    window.addEventListener('resize', this.setSectionWidth);
    await this.SET_CONTENT_ITEM_QUERY_PARAM({
      page: this.paging.currentPage,
      pageSize: this.paging.perPageSelected,
    });
    window.addEventListener('keydown', this.handleKeydown);
    this.fetchArticles();
  },
  async beforeDestroy() {
    window.removeEventListener('resize', this.setSectionWidth);
    await this.RESET_CONTENT_ITEMS_QUERY_PARAMS();
    window.removeEventListener('keydown', this.handleKeydown);
    this.SET_SELECTED_ARTICLE_UUID('');
  },
  watch: {
    selectedCreativeId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.FETCH_CONTENT_COMMENTS_COUNT({ campaignId: this.campaign.id, creativeId: this.selectedCreativeId });
      }
    },
  },
};
</script>
<style scoped lang="scss">
#articles-section {
  min-width: 200px;
}
.d-grid {
  display: grid;
}
.justify-self-center {
  justify-self: center;
}
::v-deep {
  #articles-section-header {
    position: relative;
    height: 58px;
    & > [id*='-text'] {
      line-height: unset;
    }
  }

  [data-test='creatives-page-articles-pagination'] {
    margin: 0 !important;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    > * {
      margin: 0 !important;
    }
  }
}
// Filters Buttons + Animations
#articles-filters {
  height: 0;
  overflow: hidden;
}
#articles-filters.open-filters {
  animation-name: openFilters;
  animation-duration: .5s;
  animation-fill-mode: forwards;
}
#articles-filters.close-filters {
  animation-name: closeFilters;
  animation-duration: .5s;
  animation-fill-mode: forwards;
}
#articles-filters.open-filters.share-view {
  animation-name: openFiltersInShareView;
}
#articles-filters.close-filters.share-view {
  animation-name: closeFiltersInShareView;
}
@keyframes openFilters {
  from {
    height: 0;
  }
  to {
    height: 300px;
  }
}
@keyframes closeFilters {
  from {
    height: 300px;
  }
  to {
    height: 0;
  }
}
@keyframes openFiltersInShareView {
  from {
    height: 0;
  }
  to {
    height: 220px;
  }
}
@keyframes closeFiltersInShareView {
  from {
    height: 220px;
  }
  to {
    height: 0;
  }
}
</style>
